// src/app/people/students/page.tsx - Updated with title and last name sorting
'use client';

import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Skeleton,
  Collapse,
  IconButton,
  Link as MuiLink,
  Divider,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ExpandMore,
  ExpandLess,
  Email,
  School,
  Science,
  Groups,
  Psychology,
  Person,
} from '@mui/icons-material';
import Image from 'next/image';
import Link from 'next/link';

// Import API functions
import { getMediaUrl } from '@/lib/api';

// Import our modular color system
import { withOpacity } from '@/lib/colors';
import { useColorTheme } from '@/components/ColorModeProvider';
import PageHero from '@/components/PageHero';

// Person interface
interface Person {
  id: number;
  name: string;
  title: string;
  email?: string;
  bio?: string;
  personType: string;
  program?: string;
  projectTitle?: string;
  testimonial?: string;
  affiliation?: {
    name: string;
    website?: string;
  };
  photo?: {
    url: string;
    formats?: {
      thumbnail?: { url: string };
      small?: { url: string };
    };
  } | null;
  researchThemes?: Array<{
    id: number;
    title: string;
    themeNumber: string;
  }>;
  researchAreas?: Array<{
    id: number;
    area: string;
    description?: string;
  }>;
  supervisors?: Person[];
}

// =====================
// UTILITY FUNCTIONS
// =====================

const sortStudentsByTitleAndName = (students: Person[]) => {
  // Define title priority order
  const titlePriority: Record<string, number> = {
    'Post-doctoral fellow': 1,
    'Postdoctoral fellow': 1,
    'Post-doc': 1,
    'Postdoc': 1,
    'Ph.D. student': 2,
    'PhD student': 2,
    'PhD Student': 2,
    'Doctoral student': 2,
    'MCS student': 3,
    'MCS Student': 3,
    'MSc. in Management': 4,
    'MSc in Management': 4,
    'MSc Management': 4,
    'Summer undergrad': 5,
    'Summer Undergrad': 5,
    'Undergraduate': 5,
    'Undergrad': 5,
  };

  const getTitlePriority = (title: string) => {
    // Check for exact matches first
    if (titlePriority[title]) {
      return titlePriority[title];
    }
    
    // Check for partial matches (case insensitive)
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('post-doc') || lowerTitle.includes('postdoc')) return 1;
    if (lowerTitle.includes('ph.d') || lowerTitle.includes('phd') || lowerTitle.includes('doctoral')) return 2;
    if (lowerTitle.includes('mcs')) return 3;
    if (lowerTitle.includes('msc') && lowerTitle.includes('management')) return 4;
    if (lowerTitle.includes('undergrad') || lowerTitle.includes('summer')) return 5;
    
    // Default to end if no match
    return 999;
  };

  const getLastName = (fullName: string) => {
    const nameParts = fullName.trim().split(/\s+/);
    return nameParts[nameParts.length - 1].toLowerCase();
  };

  return students.sort((a, b) => {
    const aPriority = getTitlePriority(a.title);
    const bPriority = getTitlePriority(b.title);
    
    // Primary sort: by title priority
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    
    // Secondary sort: by last name
    const aLastName = getLastName(a.name);
    const bLastName = getLastName(b.name);
    
    return aLastName.localeCompare(bLastName);
  });
};

// =====================
// STYLED COMPONENTS
// =====================

const StudentCard = styled(Card)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: '16px',
    marginBottom: '24px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${colors.effects.glow}`,
    },
  };
});

const StudentAvatar = styled(Avatar)(({ theme }) => {
  const colors = theme.colors;
  return {
    width: 90,
    height: 90,
    border: `3px solid ${withOpacity('#66bb6a', 0.3)}`,
    fontSize: '1.8rem',
    fontWeight: 600,
    
    '&:hover': {
      borderColor: '#66bb6a',
    },
  };
});

const StudentTypeChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'studentTitle',
})<{ studentTitle: string }>(({ studentTitle }) => {
  // Color coding based on student level
  const getColors = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('post-doc') || lowerTitle.includes('postdoc')) {
      return { bg: withOpacity('#9c27b0', 0.2), color: '#9c27b0', border: '#9c27b0' };
    }
    if (lowerTitle.includes('ph.d') || lowerTitle.includes('phd') || lowerTitle.includes('doctoral')) {
      return { bg: withOpacity('#f44336', 0.2), color: '#f44336', border: '#f44336' };
    }
    if (lowerTitle.includes('mcs')) {
      return { bg: withOpacity('#2196f3', 0.2), color: '#2196f3', border: '#2196f3' };
    }
    if (lowerTitle.includes('msc') && lowerTitle.includes('management')) {
      return { bg: withOpacity('#ff9800', 0.2), color: '#ff9800', border: '#ff9800' };
    }
    if (lowerTitle.includes('undergrad') || lowerTitle.includes('summer')) {
      return { bg: withOpacity('#4caf50', 0.2), color: '#4caf50', border: '#4caf50' };
    }
    // Default green for current students
    return { bg: withOpacity('#66bb6a', 0.2), color: '#66bb6a', border: '#66bb6a' };
  };
  
  const colors = getColors(studentTitle);
  
  return {
    background: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
    fontSize: '0.75rem',
    fontWeight: 500,
  };
});

const ResearchAreaChip = styled(Chip)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: withOpacity(colors.brand.accent.main, 0.1),
    color: colors.text.secondary,
    border: `1px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
    margin: '2px',
    fontSize: '0.7rem',
    height: '24px',
    
    '&:hover': {
      background: withOpacity(colors.brand.accent.main, 0.2),
      borderColor: colors.border.accent,
    },
  };
});

const TestimonialBox = styled(Box)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: withOpacity(colors.brand.accent.main, 0.05),
    border: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}`,
    borderRadius: '8px',
    padding: '16px',
    marginTop: '16px',
    position: 'relative',
    
    '&::before': {
      content: '"',
      position: 'absolute',
      top: -10,
      left: 10,
      fontSize: '3rem',
      color: colors.brand.accent.dark,
      fontFamily: 'serif',
      lineHeight: 1,
    },
  };
});

// =====================
// SUB-COMPONENTS
// =====================

interface StudentItemProps {
  student: Person;
  isExpanded: boolean;
  onToggle: () => void;
}

const StudentItem = React.memo<StudentItemProps>(({ student, isExpanded, onToggle }) => {
  const colors = useColorTheme();
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const getYearFromProgram = (program?: string) => {
    if (!program) return null;
    const yearMatch = program.match(/Year (\d+)/i);
    return yearMatch ? `Year ${yearMatch[1]}` : null;
  };

  const getDegreeFromProgram = (program?: string) => {
    if (!program) return null;
    if (program.includes('PhD')) return 'PhD';
    if (program.includes('MSc')) return 'MSc';
    if (program.includes('Masters')) return 'Masters';
    return program.split(',')[0];
  };

  return (
    <StudentCard onClick={onToggle}>
      <CardContent sx={{ p: 3 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 2 }}>
          {/* Avatar */}
          <StudentAvatar>
            {student.photo ? (
              <Image
                src={getMediaUrl(student.photo)}
                alt={student.name}
                unoptimized={true}
                width={90}
                height={90}
                style={{ objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : (
              getInitials(student.name)
            )}
          </StudentAvatar>

          {/* Basic Info */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <StudentTypeChip
                icon={<School />}
                label={student.title}
                size="small"
                studentTitle={student.title}
              />
              {getYearFromProgram(student.program) && (
                <Chip
                  label={getYearFromProgram(student.program)}
                  size="small"
                  sx={{
                    background: withOpacity('#42a5f5', 0.2),
                    color: '#42a5f5',
                    border: '1px solid #42a5f5',
                    fontSize: '0.7rem',
                  }}
                />
              )}
            </Box>

            <Typography
              variant="h6"
              sx={{
                color: colors.brand.accent.dark,
                fontWeight: 600,
                marginBottom: 0.5,
                lineHeight: 1.2,
                fontSize: '1.25rem',
              }}
            >
              {student.name}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: colors.text.secondary,
                marginBottom: 0.5,
                fontWeight: 500,
              }}
            >
              {getDegreeFromProgram(student.program) || student.title}
            </Typography>

            {student.affiliation && (
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.disabled,
                  display: 'block',
                }}
              >
                {student.affiliation.name}
              </Typography>
            )}
          </Box>

          {/* Expand/Collapse Button */}
          <IconButton
            size="small"
            sx={{
              color: colors.brand.accent.dark,
              '&:hover': {
                background: withOpacity(colors.brand.accent.main, 0.1),
              }
            }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Always Visible Student Info */}
        <Box sx={{ mb: 2 }}>
          {student.projectTitle && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <Science sx={{ fontSize: '1rem', color: colors.text.disabled, mt: 0.1 }} />
              <Typography variant="body2" sx={{ color: colors.text.secondary, fontWeight: 500 }}>
                <strong>Research Project:</strong> {student.projectTitle}
              </Typography>
            </Box>
          )}

          {student.supervisors && student.supervisors.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Groups sx={{ fontSize: '1rem', color: colors.text.disabled, mt: 0.1 }} />
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                <strong>Supervisor{student.supervisors.length > 1 ? 's' : ''}:</strong>{' '}
                {student.supervisors.map(s => s.name).join(', ')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Expandable Content */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ borderColor: withOpacity(colors.brand.accent.main, 0.2), mb: 3 }} />

          {/* Bio */}
          {student.bio && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: colors.brand.accent.dark,
                  fontWeight: 600,
                  fontSize: '1rem',
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Psychology sx={{ fontSize: '1.2rem' }} />
                About
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {student.bio}
              </Typography>
            </Box>
          )}

          {/* Research Areas */}
          {student.researchAreas && student.researchAreas.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: colors.brand.accent.dark,
                  fontWeight: 600,
                  fontSize: '1rem',
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Science sx={{ fontSize: '1.2rem' }} />
                Research Areas
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {student.researchAreas.map((area) => (
                  <ResearchAreaChip
                    key={area.id}
                    label={area.area}
                    size="small"
                    title={area.description}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Research Themes */}
          {student.researchThemes && student.researchThemes.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: colors.brand.accent.dark,
                  fontWeight: 600,
                  fontSize: '1rem',
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Science sx={{ fontSize: '1.2rem' }} />
                Research Themes
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {student.researchThemes.map((theme) => (
                  <Chip
                    key={theme.id}
                    label={`${theme.themeNumber}: ${theme.title}`}
                    size="small"
                    component={Link}
                    href={`/research-themes#${theme.themeNumber}`}
                    clickable
                    sx={{
                      background: withOpacity(colors.brand.accent.main, 0.1),
                      color: colors.text.secondary,
                      border: `1px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
                      fontSize: '0.8rem',
                      height: '28px',
                      textDecoration: 'none',
                      justifyContent: 'flex-start',
                      '&:hover': {
                        background: withOpacity(colors.brand.accent.main, 0.2),
                        borderColor: colors.border.accent,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Testimonial */}
          {student.testimonial && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: colors.brand.accent.dark,
                  fontWeight: 600,
                  fontSize: '1rem',
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Person sx={{ fontSize: '1.2rem' }} />
                Student Testimonial
              </Typography>
              <TestimonialBox>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.text.secondary,
                    lineHeight: 1.6,
                    fontStyle: 'italic',
                    pl: 2,
                  }}
                >
                  {student.testimonial}
                </Typography>
              </TestimonialBox>
            </Box>
          )}

          {/* Contact Info */}
          {student.email && (
            <Box sx={{ pt: 2, borderTop: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ fontSize: '1rem', color: colors.text.disabled }} />
                <MuiLink
                  href={`mailto:${student.email}`}
                  sx={{
                    color: colors.brand.accent.dark,
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  {student.email}
                </MuiLink>
              </Box>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </StudentCard>
  );
});

StudentItem.displayName = 'StudentItem';

const LoadingSkeleton = React.memo(() => {
  const colors = useColorTheme();
  
  return (
    <>
      {Array.from({ length: 6 }, (_, index) => (
        <Grid key={index} size={{ xs: 12, md: 6 }}>
          <Card sx={{ 
            background: colors.gradients.card, 
            border: `1px solid ${colors.border.primary}`,
            mb: 3,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2.5, mb: 2 }}>
                <Skeleton 
                  variant="circular" 
                  width={90} 
                  height={90} 
                  sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <Skeleton 
                      variant="rectangular" 
                      height={24} 
                      width={100} 
                      sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), borderRadius: 2 }} 
                    />
                    <Skeleton 
                      variant="rectangular" 
                      height={24} 
                      width={60} 
                      sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), borderRadius: 2 }} 
                    />
                  </Box>
                  <Skeleton 
                    variant="text" 
                    height={24} 
                    width="75%" 
                    sx={{ bgcolor: withOpacity(colors.text.primary, 0.15), mb: 0.5 }} 
                  />
                  <Skeleton 
                    variant="text" 
                    height={18} 
                    width="60%" 
                    sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 0.5 }} 
                  />
                  <Skeleton 
                    variant="text" 
                    height={16} 
                    width="50%" 
                    sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Skeleton 
                  variant="text" 
                  height={16} 
                  width="80%" 
                  sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
                />
                <Skeleton 
                  variant="text" 
                  height={16} 
                  width="70%" 
                  sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  );
});

LoadingSkeleton.displayName = 'LoadingSkeleton';

// =====================
// MAIN COMPONENT
// =====================

const StudentsPage: React.FC = () => {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [students, setStudents] = React.useState<Person[]>([]);

  const colors = useColorTheme();

  React.useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const apiBase = process.env.NEXT_PUBLIC_STRAPI_API_URL || `${process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337'}/api`;
        const response = await fetch(`${apiBase}/people?populate=*&pagination[pageSize]=100`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('People API response:', data);
        
        if (data.data && Array.isArray(data.data)) {
          // Filter for students only
          const filteredStudents = data.data.filter((person: Person) => 
            person.personType === 'Current Student'
          );
          
          // Sort by title priority and then by last name
          const sortedStudents = sortStudentsByTitleAndName(filteredStudents);
          
          setStudents(sortedStudents);
        } else {
          setStudents([]);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to fetch students:', err);
        setError(err instanceof Error ? err.message : 'Failed to load students');
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  const handleCardToggle = useCallback((personId: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(personId)) {
        newSet.delete(personId);
      } else {
        newSet.add(personId);
      }
      return newSet;
    });
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      <PageHero
        label="People"
        title="Current Students"
        subtitle="Meet our talented graduate students advancing cybersecurity research"
        icon={<School />}
      />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Single Total Students Statistic */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Box sx={{ 
            background: colors.background.elevated,
            border: `1px solid ${colors.border.primary}`,
            p: 4,
            textAlign: 'center',
            minWidth: '200px',
            borderRadius: 2,
          }}>
            <Groups sx={{ color: colors.brand.accent.dark, fontSize: '3rem', mb: 1 }} />
            <Typography variant="h3" sx={{ color: colors.brand.accent.dark, fontWeight: 700 }}>
              {students.length}
            </Typography>
            <Typography variant="h6" sx={{ color: colors.text.disabled }}>
              Current Students
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        {loading ? (
          <Grid container spacing={3}>
            <LoadingSkeleton />
          </Grid>
        ) : error ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            background: colors.gradients.card, 
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 2,
          }}>
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 2 }}>
              Unable to Load Students
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              {error}
            </Typography>
          </Box>
        ) : students.length > 0 ? (
          <Grid container spacing={3}>
            {students.map((student) => (
              <Grid key={student.id} size={{ xs: 12, md: 6 }}>
                <StudentItem
                  student={student}
                  isExpanded={expandedCards.has(student.id)}
                  onToggle={() => handleCardToggle(student.id)}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            background: colors.gradients.card, 
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 2,
          }}>
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
              No Students Found
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              Student information will appear here once it's added to the system.
            </Typography>
          </Box>
        )}

        {/* Call to Action - Hidden */}
        {students.length > 0 && (
          <Box sx={{ 
            p: 4, 
            mt: 6, 
            textAlign: 'center', 
            background: colors.background.elevated, 
            border: `1px solid ${colors.border.accent}`,
            borderRadius: 2,
            display: 'none',
          }}>
            <Typography
              variant="h5"
              sx={{
                color: colors.brand.accent.dark,
                fontWeight: 600,
                marginBottom: 2,
              }}
            >
              Interested in Joining Our Program?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.text.secondary,
                marginBottom: 3,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Learn about graduate student opportunities, application requirements, and funding options 
              in our cybersecurity research program.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box
                component={Link}
                href="/news-events"
                sx={{
                  background: colors.gradients.primary,
                  color: colors.brand.accent.contrastText,
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 1,
                  textDecoration: 'none',
                  display: 'inline-block',
                  '&:hover': {
                    background: colors.gradients.primary,
                    filter: 'brightness(1.1)',
                  }
                }}
              >
                View Opportunities
              </Box>
              
              <Box
                component={Link}
                href="/contact"
                sx={{
                  color: colors.brand.accent.dark,
                  borderColor: colors.brand.accent.main,
                  border: '1px solid',
                  px: 4,
                  py: 1.5,
                  borderRadius: 1,
                  textDecoration: 'none',
                  display: 'inline-block',
                  '&:hover': {
                    background: withOpacity(colors.brand.accent.main, 0.1),
                  }
                }}
              >
                Apply Now
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default StudentsPage;
